import type { Account, AccountLedgerEntry, BalanceAudit, Budget, CrDrEntry, MonthlyPeriod, Plan, PlanPayment, Transaction } from '../types';
import { buildBudgetUsage, type BudgetUsage } from './budgets';
import { monthKey, normalizeDateISO } from './date';
import { accountMatches, budgetInRange, inDateRange, ledgerAccountMatches, paymentAccountMatches, planInRange, type AccountFilter } from './reportFilters';
import { buildAccountStatement } from './statement';

export type ReportPreset = 'This Month' | 'Last Month' | 'This Quarter' | 'This Financial Year' | 'Last Financial Year' | 'Custom';
export type ReportSection = 'overview' | 'spending' | 'accounts' | 'plans' | 'movements' | 'crdr' | 'budgets' | 'tax' | 'audits' | 'transactions';
export type ExportFormat = 'pdf' | 'excel' | 'csv';
export type ExportScope = 'full' | 'current';

export type ReportFilters = {
  preset: ReportPreset;
  fromDate: string;
  toDate: string;
  accountFilter: AccountFilter;
  transactionType: 'All' | 'Income' | 'Expense' | 'Investment';
  category: string;
  paymentMode: string;
  person: string;
};

export type ReportSourceData = {
  transactions: Transaction[];
  payments: PlanPayment[];
  plans: Plan[];
  crdr: CrDrEntry[];
  budgets: Budget[];
  accounts: Account[];
  accountLedger: AccountLedgerEntry[];
  balanceAudits: BalanceAudit[];
  monthlyPeriods?: MonthlyPeriod[];
};

export type GroupMetric = {
  name: string;
  count: number;
  amount: number;
  percent: number;
  average: number;
};

export type AccountReportRow = {
  account: Account;
  opening: number;
  credits: number;
  debits: number;
  closing: number;
  netMovement: number;
  status: 'healthy' | 'negative' | 'archived' | 'credit_card';
};

export type ReportsModel = {
  filters: ReportFilters;
  rangeTx: Transaction[];
  rangePayments: PlanPayment[];
  rangePlans: Plan[];
  rangeCrdr: CrDrEntry[];
  rangeLedger: AccountLedgerEntry[];
  rangeAudits: BalanceAudit[];
  activeAccounts: Account[];
  income: number;
  expense: number;
  investment: number;
  netCashFlow: number;
  totalCredits: number;
  totalDebits: number;
  transactionCount: number;
  categoryExpense: Array<[string, number]>;
  paymentModeExpense: Array<[string, number]>;
  categoryMetrics: GroupMetric[];
  paymentModeMetrics: GroupMetric[];
  topExpenses: Transaction[];
  highestExpense?: Transaction;
  largestOutgoing?: Transaction;
  accountRows: AccountReportRow[];
  accountNetMovement: number;
  highestSpendAccount?: { name: string; amount: number };
  liquidBalance: number;
  creditOutstanding: number;
  availableCredit: number;
  todaySpend: number;
  scheduledPlansTotal: number;
  paidTotal: number;
  unpaidPlanTotal: number;
  overduePlans: Plan[];
  overduePlanTotal: number;
  nextDueDate: string;
  plansByCategory: GroupMetric[];
  plansByStatus: GroupMetric[];
  planPaymentsByAccount: Array<[string, number]>;
  transfers: AccountLedgerEntry[];
  cardPayments: AccountLedgerEntry[];
  transferTotal: number;
  cardPaymentTotal: number;
  cr: number;
  dr: number;
  crdrBalance: number;
  crdrRows: Array<{ person: string; receivable: number; payable: number; net: number; openItems: number; lastActivity: string }>;
  budgetUsage: BudgetUsage[];
  budgetTotal: number;
  budgetSpent: number;
  budgetRemaining: number;
  budgetExceeded: number;
  budgetWarning: number;
  taxRows: Transaction[];
  taxIncome: Array<[string, number]>;
  taxExpense: Array<[string, number]>;
  taxInvestment: Array<[string, number]>;
  taxIgnored: Array<[string, number]>;
  auditVerified: number;
  auditMismatches: number;
  latestAuditDate: string;
  insights: Array<{ title: string; detail: string; tone?: 'good' | 'warn' | 'danger' | 'neutral' }>;
};

const sum = <T,>(rows: T[], pick: (row: T) => number) => rows.reduce((total, row) => total + Number(pick(row) || 0), 0);

const startOfMonth = (date: string) => `${date.slice(0, 7)}-01`;
const localDateISO = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const daysInMonth = (year: number, month: number) => new Date(year, month, 0).getDate();
const endOfMonth = (date: string) => {
  const year = Number(date.slice(0, 4));
  const month = Number(date.slice(5, 7));
  return `${year}-${String(month).padStart(2, '0')}-${String(daysInMonth(year, month)).padStart(2, '0')}`;
};
const shiftMonth = (date: string, offset: number) => {
  const year = Number(date.slice(0, 4));
  const monthIndex = Number(date.slice(5, 7)) - 1 + offset;
  const shifted = new Date(year, monthIndex, 1);
  return `${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}-01`;
};

export const reportSections: Array<{ key: ReportSection; title: string; description: string }> = [
  { key: 'overview', title: 'Overview', description: 'Financial KPIs, insights, and compact trend summary.' },
  { key: 'spending', title: 'Spending Analysis', description: 'Categories, payment modes, averages, and top expenses.' },
  { key: 'accounts', title: 'Account Analysis', description: 'Opening, credits, debits, closing, and net movement per account.' },
  { key: 'plans', title: 'Plans & Commitments', description: 'Scheduled, paid, unpaid, overdue, and payment history.' },
  { key: 'movements', title: 'Transfers & Card Payments', description: 'Transfers and card payments without income/expense double counting.' },
  { key: 'crdr', title: 'CR/DR Analysis', description: 'Receivable, payable, and person-wise open position.' },
  { key: 'budgets', title: 'Budgets', description: 'Budget usage, remaining amount, and exceeded categories.' },
  { key: 'tax', title: 'Tax Summary', description: 'User-tagged income, expenses, investments, and ignored rows.' },
  { key: 'audits', title: 'Balance Audits', description: 'Verified balances, mismatches, and latest audit snapshots.' },
  { key: 'transactions', title: 'Transaction Detail', description: 'Filtered transaction detail for review and export context.' }
];

export const reportPresets: ReportPreset[] = ['This Month', 'Last Month', 'This Quarter', 'This Financial Year', 'Last Financial Year', 'Custom'];

export function rangeFromReportPreset(preset: ReportPreset, now = localDateISO()) {
  const today = normalizeDateISO(now);
  const year = Number(today.slice(0, 4));
  const month = Number(today.slice(5, 7));
  if (preset === 'Last Month') {
    const lastMonth = shiftMonth(today, -1);
    return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
  }
  if (preset === 'This Quarter') {
    const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
    const quarterEndMonth = quarterStartMonth + 2;
    const start = `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
    const endDay = daysInMonth(year, quarterEndMonth);
    return { from: start, to: `${year}-${String(quarterEndMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}` };
  }
  if (preset === 'This Financial Year') {
    const fyStartYear = month >= 4 ? year : year - 1;
    return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31` };
  }
  if (preset === 'Last Financial Year') {
    const fyStartYear = (month >= 4 ? year : year - 1) - 1;
    return { from: `${fyStartYear}-04-01`, to: `${fyStartYear + 1}-03-31` };
  }
  return { from: startOfMonth(today), to: endOfMonth(today) };
}

export function defaultReportFilters(now = localDateISO()): ReportFilters {
  const range = rangeFromReportPreset('This Month', now);
  return { preset: 'This Month', fromDate: range.from, toDate: range.to, accountFilter: 'all', transactionType: 'All', category: 'All', paymentMode: 'All', person: '' };
}

export function normalizeReportFilters(input: Partial<ReportFilters>, now = localDateISO()): ReportFilters {
  const base = defaultReportFilters(now);
  const preset = input.preset || base.preset;
  const presetRange = preset === 'Custom' ? { from: base.fromDate, to: base.toDate } : rangeFromReportPreset(preset, now);
  const fromDate = normalizeDateISO(preset === 'Custom' ? input.fromDate || presetRange.from : presetRange.from);
  const toDate = normalizeDateISO(preset === 'Custom' ? input.toDate || presetRange.to : presetRange.to);
  return {
    preset,
    fromDate: fromDate <= toDate ? fromDate : toDate,
    toDate: fromDate <= toDate ? toDate : fromDate,
    accountFilter: input.accountFilter || 'all',
    transactionType: input.transactionType || 'All',
    category: input.category || 'All',
    paymentMode: input.paymentMode || 'All',
    person: input.person || ''
  };
}

export function resolveReportSection(hash = ''): ReportSection {
  const key = hash.replace(/^#\/?reports\/?/, '').replace(/^#/, '').trim().toLowerCase();
  const aliases: Record<string, ReportSection> = {
    overview: 'overview',
    spending: 'spending',
    accounts: 'accounts',
    account: 'accounts',
    plans: 'plans',
    commitments: 'plans',
    movements: 'movements',
    transfers: 'movements',
    crdr: 'crdr',
    budgets: 'budgets',
    tax: 'tax',
    audits: 'audits',
    transactions: 'transactions'
  };
  return aliases[key] || 'overview';
}

export const reportSectionHash = (section: ReportSection) => section === 'overview' ? '#reports' : `#reports/${section}`;

const groupMetrics = <T,>(rows: T[], key: (row: T) => string, amount: (row: T) => number, baseTotal?: number): GroupMetric[] => {
  const grouped = new Map<string, { count: number; amount: number }>();
  rows.forEach((row) => {
    const name = key(row) || 'Uncategorized';
    const current = grouped.get(name) || { count: 0, amount: 0 };
    current.count += 1;
    current.amount += Number(amount(row) || 0);
    grouped.set(name, current);
  });
  const total = baseTotal ?? Array.from(grouped.values()).reduce((value, row) => value + row.amount, 0);
  return Array.from(grouped.entries())
    .map(([name, row]) => ({ name, count: row.count, amount: row.amount, percent: total > 0 ? Math.round((row.amount / total) * 100) : 0, average: row.count ? row.amount / row.count : 0 }))
    .sort((a, b) => b.amount - a.amount || b.count - a.count || a.name.localeCompare(b.name));
};

const amountEntries = (metrics: GroupMetric[]) => metrics.map((metric) => [metric.name, metric.amount] as [string, number]);

const statusKey = (plan: Plan, now: string) => {
  const due = normalizeDateISO(plan.nextDueDate || plan.dueDate);
  if (due < now) return 'Overdue';
  return String(plan.status || 'active');
};

export function buildReportsModel(source: ReportSourceData, inputFilters: Partial<ReportFilters>, now = localDateISO()): ReportsModel {
  const filters = normalizeReportFilters(inputFilters, now);
  const personNeedle = filters.person.trim().toLowerCase();
  const rangeTx = source.transactions
    .filter((row) => row.status !== 'deleted' && row.active !== false)
    .filter((row) => inDateRange(row.dateISO || row.date, filters.fromDate, filters.toDate))
    .filter((row) => accountMatches(filters.accountFilter, row))
    .filter((row) => filters.transactionType === 'All' || row.type === filters.transactionType)
    .filter((row) => filters.category === 'All' || row.category === filters.category)
    .filter((row) => filters.paymentMode === 'All' || row.paymentMode === filters.paymentMode)
    .sort((a, b) => normalizeDateISO(b.dateISO || b.date).localeCompare(normalizeDateISO(a.dateISO || a.date)) || String(b.id || '').localeCompare(String(a.id || '')));
  const rangePayments = source.payments
    .filter((row) => inDateRange(row.paidDate, filters.fromDate, filters.toDate))
    .filter((row) => paymentAccountMatches(filters.accountFilter, row));
  const rangePlans = source.plans
    .filter((plan) => plan.status !== 'deleted' && plan.active !== false)
    .filter((plan) => planInRange(plan, filters.fromDate, filters.toDate));
  const rangeCrdr = source.crdr
    .filter((row) => inDateRange(row.date, filters.fromDate, filters.toDate))
    .filter((row) => !personNeedle || [row.contactName, row.name, row.contactPhone, row.contactEmail].some((value) => String(value || '').toLowerCase().includes(personNeedle)));
  const rangeBudgets = source.budgets.filter((budget) => budgetInRange(budget.monthKey, filters.fromDate, filters.toDate));
  const activeAccounts = source.accounts.filter((account) => account.status !== 'deleted');
  const rangeLedger = source.accountLedger
    .filter((row) => inDateRange(row.dateISO || row.date, filters.fromDate, filters.toDate))
    .filter((row) => ledgerAccountMatches(filters.accountFilter, row, activeAccounts));
  const rangeAudits = source.balanceAudits
    .filter((row) => inDateRange(row.dateISO || row.auditDate, filters.fromDate, filters.toDate))
    .filter((row) => accountMatches(filters.accountFilter, row));

  const incomeRows = rangeTx.filter((row) => row.type === 'Income');
  const expenseRows = rangeTx.filter((row) => row.type === 'Expense');
  const investmentRows = rangeTx.filter((row) => row.type === 'Investment');
  const income = sum(incomeRows, (row) => row.amount);
  const expense = sum(expenseRows, (row) => row.amount);
  const investment = sum(investmentRows, (row) => row.amount);
  const categoryMetrics = groupMetrics(expenseRows, (row) => row.category, (row) => row.amount, expense);
  const paymentModeMetrics = groupMetrics(expenseRows, (row) => row.paymentMode, (row) => row.amount, expense);
  const topExpenses = [...expenseRows].sort((a, b) => b.amount - a.amount || normalizeDateISO(b.dateISO || b.date).localeCompare(normalizeDateISO(a.dateISO || a.date))).slice(0, 10);

  const includedAccounts = activeAccounts.filter((account) => {
    if (filters.accountFilter === 'all') return true;
    if (filters.accountFilter === 'liquid') return account.type !== 'credit_card';
    if (filters.accountFilter === 'credit_cards') return account.type === 'credit_card';
    if (filters.accountFilter === 'unassigned') return false;
    return account.id === filters.accountFilter;
  });
  const accountRows = includedAccounts.map((account) => {
    const statement = buildAccountStatement(account, source.accountLedger.filter((row) => row.accountId === account.id), filters.fromDate, filters.toDate, 'oldest');
    const status: AccountReportRow['status'] = account.status === 'archived' ? 'archived' : account.type === 'credit_card' ? 'credit_card' : statement.closingBalance < 0 ? 'negative' : 'healthy';
    return { account, opening: statement.openingBalance, credits: statement.totalCredit, debits: statement.totalDebit, closing: statement.closingBalance, netMovement: statement.netMovement, status };
  });

  const plansByCategory = groupMetrics(rangePlans, (plan) => plan.category, (plan) => plan.amount);
  const plansByStatus = groupMetrics(rangePlans, (plan) => statusKey(plan, now), (plan) => plan.amount);
  const scheduledPlansTotal = sum(rangePlans, (plan) => plan.amount);
  const paidTotal = sum(rangePayments, (row) => row.amount);
  const overduePlans = rangePlans.filter((plan) => normalizeDateISO(plan.nextDueDate || plan.dueDate) < now);
  const budgetUsage = buildBudgetUsage(rangeBudgets, rangeTx);
  const budgetTotal = sum(budgetUsage, (budget) => budget.limitAmount);
  const budgetSpent = sum(budgetUsage, (budget) => budget.spent);

  const transfers = rangeLedger.filter((row) => row.type === 'transfer_out');
  const cardPayments = rangeLedger.filter((row) => row.type === 'credit_card_payment' && row.direction === 'debit');
  const cr = sum(rangeCrdr.filter((row) => row.type === 'CR'), (row) => row.amount);
  const dr = sum(rangeCrdr.filter((row) => row.type === 'DR'), (row) => row.amount);
  const crdrRows = Array.from(rangeCrdr.reduce((map, row) => {
    const person = row.contactName || row.name || 'Unknown person';
    const current = map.get(person) || { person, receivable: 0, payable: 0, net: 0, openItems: 0, lastActivity: '' };
    if (row.type === 'CR') current.receivable += row.amount;
    if (row.type === 'DR') current.payable += row.amount;
    current.net = current.receivable - current.payable;
    if (row.status !== 'Settled') current.openItems += 1;
    current.lastActivity = current.lastActivity > row.date ? current.lastActivity : row.date;
    map.set(person, current);
    return map;
  }, new Map<string, { person: string; receivable: number; payable: number; net: number; openItems: number; lastActivity: string }>()).values()).sort((a, b) => Math.abs(b.net) - Math.abs(a.net) || a.person.localeCompare(b.person));

  const taxRows = rangeTx.filter((row) => row.taxTag);
  const taxIncome = amountEntries(groupMetrics(taxRows.filter((row) => row.type === 'Income' && row.taxTag !== 'Ignore for ITR'), (row) => row.taxTag || 'Untagged', (row) => row.amount));
  const taxExpense = amountEntries(groupMetrics(taxRows.filter((row) => row.type === 'Expense' && row.taxTag !== 'Ignore for ITR'), (row) => row.taxTag || 'Untagged', (row) => row.amount));
  const taxInvestment = amountEntries(groupMetrics(taxRows.filter((row) => row.type === 'Investment' && row.taxTag !== 'Ignore for ITR'), (row) => row.taxTag || 'Untagged', (row) => row.amount));
  const taxIgnored = amountEntries(groupMetrics(taxRows.filter((row) => row.taxTag === 'Ignore for ITR'), (row) => row.category || 'Ignored', (row) => row.amount));
  const auditMismatches = rangeAudits.filter((row) => Math.abs(Number(row.difference || 0)) > 0 || Math.abs(Number(row.availableLimitDifference || 0)) > 0).length;
  const highestSpendAccount = groupMetrics(expenseRows.filter((row) => row.accountName), (row) => row.accountName || 'Unassigned', (row) => row.amount)[0];
  const largestOutgoing = [...rangeTx.filter((row) => row.type !== 'Income')].sort((a, b) => b.amount - a.amount)[0];
  const insights = [
    categoryMetrics[0] ? { title: 'Largest spending category', detail: `${categoryMetrics[0].name} is ${categoryMetrics[0].percent}% of expenses.`, tone: 'neutral' as const } : undefined,
    highestSpendAccount ? { title: 'Highest-spend account', detail: `${highestSpendAccount.name} has the highest expense movement.`, tone: 'neutral' as const } : undefined,
    expense > income ? { title: 'Expense exceeded income', detail: `Expenses are higher than income in this period.`, tone: 'danger' as const } : { title: 'Income coverage', detail: `Income covers selected expenses.`, tone: 'good' as const },
    overduePlans.length ? { title: 'Overdue commitments', detail: `${overduePlans.length} plan(s) are overdue.`, tone: 'warn' as const } : undefined,
    budgetUsage.filter((budget) => budget.state === 'crossed').length ? { title: 'Budget crossed', detail: `${budgetUsage.filter((budget) => budget.state === 'crossed').length} budget category/categories crossed 100%.`, tone: 'danger' as const } : undefined,
    auditMismatches ? { title: 'Audit mismatch', detail: `${auditMismatches} balance audit mismatch(es) need review.`, tone: 'warn' as const } : undefined,
    largestOutgoing ? { title: 'Largest outgoing', detail: `${largestOutgoing.category} is the largest outgoing transaction.`, tone: 'neutral' as const } : undefined
  ].filter(Boolean) as ReportsModel['insights'];

  return {
    filters,
    rangeTx,
    rangePayments,
    rangePlans,
    rangeCrdr,
    rangeLedger,
    rangeAudits,
    activeAccounts,
    income,
    expense,
    investment,
    netCashFlow: income - expense - investment,
    totalCredits: income,
    totalDebits: expense + investment,
    transactionCount: rangeTx.length,
    categoryExpense: amountEntries(categoryMetrics),
    paymentModeExpense: amountEntries(paymentModeMetrics),
    categoryMetrics,
    paymentModeMetrics,
    topExpenses,
    highestExpense: topExpenses[0],
    largestOutgoing,
    accountRows,
    accountNetMovement: sum(accountRows, (row) => row.netMovement),
    highestSpendAccount: highestSpendAccount ? { name: highestSpendAccount.name, amount: highestSpendAccount.amount } : undefined,
    liquidBalance: sum(activeAccounts.filter((account) => account.type !== 'credit_card'), (account) => account.currentBalance),
    creditOutstanding: sum(activeAccounts.filter((account) => account.type === 'credit_card'), (account) => account.currentBalance),
    availableCredit: sum(activeAccounts.filter((account) => account.type === 'credit_card'), (account) => Number(account.availableLimit || 0)),
    todaySpend: sum(rangeTx.filter((row) => row.dateISO === now && (row.type === 'Expense' || row.type === 'Investment')), (row) => row.amount),
    scheduledPlansTotal,
    paidTotal,
    unpaidPlanTotal: Math.max(scheduledPlansTotal - paidTotal, 0),
    overduePlans,
    overduePlanTotal: sum(overduePlans, (plan) => plan.amount),
    nextDueDate: rangePlans[0] ? normalizeDateISO(rangePlans[0].nextDueDate || rangePlans[0].dueDate) : '',
    plansByCategory,
    plansByStatus,
    planPaymentsByAccount: amountEntries(groupMetrics(rangePayments, (row) => row.paidFromAccountName || 'Unassigned', (row) => row.amount)),
    transfers,
    cardPayments,
    transferTotal: sum(transfers, (row) => row.amount),
    cardPaymentTotal: sum(cardPayments, (row) => row.amount),
    cr,
    dr,
    crdrBalance: cr - dr,
    crdrRows,
    budgetUsage,
    budgetTotal,
    budgetSpent,
    budgetRemaining: budgetTotal - budgetSpent,
    budgetExceeded: budgetUsage.filter((budget) => budget.state === 'crossed').length,
    budgetWarning: budgetUsage.filter((budget) => budget.state === 'warning').length,
    taxRows,
    taxIncome,
    taxExpense,
    taxInvestment,
    taxIgnored,
    auditVerified: rangeAudits.length - auditMismatches,
    auditMismatches,
    latestAuditDate: rangeAudits[0]?.dateISO || rangeAudits[0]?.auditDate || '',
    insights
  };
}

export function buildReportSectionCards(model: ReportsModel) {
  return [
    { key: 'overview' as const, title: 'Overview', metric: `${model.transactionCount} transaction(s)`, sub: `Net ${model.netCashFlow}`, status: model.netCashFlow >= 0 ? 'positive' : 'warning' },
    { key: 'spending' as const, title: 'Spending Analysis', metric: model.categoryMetrics[0] ? `Top: ${model.categoryMetrics[0].name}` : 'No expenses', sub: `${model.expense} across ${model.topExpenses.length} top rows`, status: model.expense ? 'active' : 'empty' },
    { key: 'accounts' as const, title: 'Account Analysis', metric: `${model.accountRows.length} accounts`, sub: `${model.accountNetMovement} net movement`, status: model.accountRows.some((row) => row.status === 'negative') ? 'warning' : 'active' },
    { key: 'plans' as const, title: 'Plans & Commitments', metric: `${model.rangePlans.length} due`, sub: `${model.overduePlans.length} overdue`, status: model.overduePlans.length ? 'warning' : 'active' },
    { key: 'movements' as const, title: 'Transfers & Card Payments', metric: `${model.transfers.length} transfers`, sub: `${model.cardPayments.length} card payments`, status: model.transfers.length || model.cardPayments.length ? 'active' : 'empty' },
    { key: 'crdr' as const, title: 'CR/DR Analysis', metric: `${model.crdrRows.length} people`, sub: `${model.crdrBalance} net`, status: model.crdrRows.length ? 'active' : 'empty' },
    { key: 'budgets' as const, title: 'Budgets', metric: `${model.budgetUsage.length} budgets`, sub: `${model.budgetExceeded} exceeded`, status: model.budgetExceeded ? 'warning' : model.budgetUsage.length ? 'active' : 'empty' },
    { key: 'tax' as const, title: 'Tax Summary', metric: `${model.taxRows.length} tagged rows`, sub: 'User-entered tax tags', status: model.taxRows.length ? 'active' : 'empty' },
    { key: 'audits' as const, title: 'Balance Audits', metric: `${model.rangeAudits.length} audits`, sub: `${model.auditMismatches} mismatch`, status: model.auditMismatches ? 'warning' : model.rangeAudits.length ? 'active' : 'empty' },
    { key: 'transactions' as const, title: 'Transaction Detail', metric: `${model.rangeTx.length} rows`, sub: `${model.totalCredits - model.totalDebits} net movement`, status: model.rangeTx.length ? 'active' : 'empty' }
  ];
}

export function resolveExportSections(scope: ExportScope, current: ReportSection, selected: ReportSection[]) {
  if (scope === 'current') return [current];
  return selected.length ? Array.from(new Set(selected)) : reportSections.map((section) => section.key);
}
